import { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PointsPanel from '../points/PointsPanel';

/**
 * Mobile-only view of the live points list.
 *
 * A collapsed count pill rather than a persistent panel: on a phone the chat
 * column is already cramped, and an always-open drawer would fight the on-screen
 * keyboard. The pill gives ambient awareness (the count ticks up and pulses when
 * a point lands) and is one thumb-tap from the full list.
 */
export default function PointsDrawer({ points = [], extracting = false }) {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const prevCount = useRef(points.length);

  useEffect(() => {
    if (points.length > prevCount.current) {
      setPulse(true);
      const timer = setTimeout(() => setPulse(false), 1200);
      prevCount.current = points.length;
      return () => clearTimeout(timer);
    }
    prevCount.current = points.length;
    return undefined;
  }, [points.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!points.length && !extracting) return null;

  return (
    <div className="lg:hidden">
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`fixed bottom-[4.5rem] right-4 z-40 flex items-center gap-2 rounded-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg text-sm font-medium text-gray-700 dark:text-gray-200 ${
            pulse ? 'animate-pulse ring-2 ring-indigo-400' : ''
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-indigo-500" />
          {points.length} {points.length === 1 ? 'point' : 'points'}
          {extracting && (
            <span className="text-xs text-gray-400 dark:text-gray-500">…</span>
          )}
        </button>
      )}

      {open && (
        <>
          <div
            role="presentation"
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/40 z-40"
          />
          <div className="fixed inset-x-0 bottom-0 z-50 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-700" />
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                Points ({points.length})
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close points"
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <PointsPanel points={points} />
            {extracting && (
              <p className="mt-3 text-xs text-gray-400 dark:text-gray-500 italic">
                Pulling out new points…
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
